// 跨局永久進度（roguelite meta）：souls（魂）= 結算賺取的永久幣，用來永久解鎖 roster 角色。
// FRONTEND-FIRST：先以 localStorage 持久化，後端權威化（連同種子 replay 結算）是 P 步驟。
// souls 與 gem（氪金硬幣）嚴格分離 —— souls 由遊玩賺取，gem 由付費購買；兩者都不破壞 PvE 公平。
import { PACK_ROSTER } from '../sim/JKGame_pack'

const KEY_SOULS = 'jkgame.souls'
const KEY_UNLOCKED = 'jkgame.unlocked' // JSON string[] of unlocked character ids (souls path)

function readNum(key: string): number {
  try {
    const n = Number(localStorage.getItem(key))
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
  } catch {
    return 0
  }
}

export function getSouls(): number {
  return readNum(KEY_SOULS)
}

export function addSouls(n: number): number {
  const next = Math.max(0, getSouls() + Math.floor(n))
  try {
    localStorage.setItem(KEY_SOULS, String(next))
  } catch {
    /* ignore */
  }
  return next
}

function readUnlocked(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY_UNLOCKED)
    const arr = raw ? (JSON.parse(raw) as unknown) : []
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [])
  } catch {
    return new Set()
  }
}

function writeUnlocked(set: Set<string>): void {
  try {
    localStorage.setItem(KEY_UNLOCKED, JSON.stringify([...set]))
  } catch {
    /* ignore */
  }
}

// Mirror the AUTHORITATIVE backend wallet (souls + unlocked set) into localStorage, so the rest of
// the client — getSouls/isUnlocked/activeLoadout/activeCharForBackend — reads server truth in
// backend mode without a second code path. localStorage becomes a read-through cache: Collection
// calls this after fetching the wallet and after every server unlock. Keeps the two stores in sync
// (no "unlocked on server, locked locally" desync). Offline, localStorage stays the source of truth.
export function syncFromBackend(soulsBalance: number, unlockedIds: string[]): void {
  try {
    localStorage.setItem(KEY_SOULS, String(Math.max(0, Math.floor(soulsBalance))))
    localStorage.setItem(KEY_UNLOCKED, JSON.stringify(unlockedIds.filter((x) => typeof x === 'string')))
  } catch {
    /* ignore */
  }
}

// Parse the soul cost from a roster `unlock` field ("souls:400" → 400). Non-souls paths → null.
export function soulCost(unlock: string): number | null {
  const m = /^souls:(\d+)$/.exec(unlock)
  return m ? Number(m[1]) : null
}

// Is a character available to deploy? free → always; souls:N → once unlocked; anything else
// (e.g. gacha/gem path) → false in frontend-first mode (becomes true via the backend later).
export function isUnlocked(charId: string): boolean {
  const c = PACK_ROSTER.find((r) => r.id === charId)
  if (!c) return false
  if (c.unlock === 'free') return true
  if (soulCost(c.unlock) !== null) return readUnlocked().has(charId)
  return false
}

export type UnlockResult = { ok: true; souls: number } | { ok: false; reason: 'notSoulPath' | 'cantAfford' | 'already' }

// Spend souls to permanently unlock a souls-path character. Pure-ish (localStorage side effect);
// returns a typed result so the UI can show a localized reason (never a silent failure).
export function unlockChar(charId: string): UnlockResult {
  const c = PACK_ROSTER.find((r) => r.id === charId)
  const cost = c ? soulCost(c.unlock) : null
  if (!c || cost === null) return { ok: false, reason: 'notSoulPath' }
  if (isUnlocked(charId)) return { ok: false, reason: 'already' }
  if (getSouls() < cost) return { ok: false, reason: 'cantAfford' }
  const souls = addSouls(-cost)
  const set = readUnlocked()
  set.add(charId)
  writeUnlocked(set)
  return { ok: true, souls }
}
